/**
 * Family handover PDF generator.
 *
 * Lazy-loads pdf-lib only when the user actually generates a handover.
 * Output: a single PDF the family receives on release, containing
 * step-by-step instructions to access every asset.
 *
 * NEVER includes raw secrets (passcodes, seed phrases, shamir shares).
 * Includes:
 *  - Asset list with institution + last-4 + nominee + custodian note
 *  - Document index
 *  - Last wishes (high-priority first)
 *  - Crypto wallet identifiers (NOT seeds; pointer to coordinator URL)
 *  - Subscription cancellation map
 *  - Empty signature block for executor
 */

import type { Vault, Asset, LastWish, Nominee } from '../types.ts';
import { formatINR, ASSET_DISPLAY, NOMINEE_DISPLAY } from '../types.ts';

interface PDFLib {
  PDFDocument: { create: () => Promise<PDFDoc> };
  StandardFonts: Record<string, string>;
  rgb: (r: number, g: number, b: number) => unknown;
}

interface PDFDoc {
  addPage: (size?: [number, number]) => PDFPage;
  embedFont: (font: string) => Promise<PDFFont>;
  save: () => Promise<Uint8Array>;
}

interface PDFPage {
  getSize: () => { width: number; height: number };
  drawText: (text: string, opts: { x: number; y: number; size: number; font?: PDFFont; color?: unknown; maxWidth?: number; lineHeight?: number }) => void;
  drawLine: (opts: { start: { x: number; y: number }; end: { x: number; y: number }; thickness?: number; color?: unknown }) => void;
  drawRectangle: (opts: { x: number; y: number; width: number; height: number; color?: unknown; borderColor?: unknown; borderWidth?: number }) => void;
}

interface PDFFont {
  widthOfTextAtSize: (text: string, size: number) => number;
}

async function loadPdfLib(): Promise<PDFLib | null> {
  try {
    // CDN ESM. Pinned version. Pure JS, no WASM. The string indirection
    // bypasses TS module resolution since the URL is not a known package.
    const url = 'https://esm.sh/pdf-lib@1.17.1';
    const mod = await (new Function('u', 'return import(u)') as (u: string) => Promise<unknown>)(url);
    return mod as PDFLib;
  } catch {
    return null;
  }
}

const A4: [number, number] = [595.28, 841.89];

export async function generateHandoverPDF(v: Vault): Promise<Uint8Array | null> {
  const pdfLib = await loadPdfLib();
  if (!pdfLib) return null;
  const doc = await pdfLib.PDFDocument.create();
  const body = await doc.embedFont(pdfLib.StandardFonts.TimesRoman ?? 'Times-Roman');
  const bold = await doc.embedFont(pdfLib.StandardFonts.TimesRomanBold ?? 'Times-Bold');
  const mono = await doc.embedFont(pdfLib.StandardFonts.Courier ?? 'Courier');
  const ink = pdfLib.rgb(0.12, 0.12, 0.15);
  const indigo = pdfLib.rgb(0.18, 0.22, 0.39);
  const crimson = pdfLib.rgb(0.55, 0.18, 0.14);
  const muted = pdfLib.rgb(0.45, 0.45, 0.50);

  let page = doc.addPage(A4);
  let y = page.getSize().height - 60;

  function nl(amount = 14): void {
    y -= amount;
    if (y < 80) {
      page = doc.addPage(A4);
      y = page.getSize().height - 60;
    }
  }

  function text(s: string, opts: { size?: number; font?: PDFFont; color?: unknown; indent?: number } = {}): void {
    page.drawText(s, {
      x: 60 + (opts.indent ?? 0),
      y,
      size: opts.size ?? 11,
      font: opts.font ?? body,
      color: opts.color ?? ink,
      maxWidth: 475,
      lineHeight: 14,
    });
  }

  function hr(color: unknown = muted): void {
    nl(8);
    page.drawLine({ start: { x: 60, y }, end: { x: 535, y }, thickness: 0.5, color });
    nl(6);
  }

  // ───────────── Cover ─────────────
  text('WASIYAT', { size: 28, font: bold, color: indigo });
  nl(34);
  text('Family handover document', { size: 14, font: body, color: muted });
  nl(22);
  text(`Generated for: ${v.owner.display_name}`, { size: 12, font: bold });
  nl();
  text(`Issued: ${new Date(v.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`, { size: 11, color: muted });
  nl(28);

  text('This document is meant for the family in the event of the holder', { size: 10, font: body, color: muted });
  nl();
  text('becoming unreachable, incapacitated, or deceased. It does NOT', { size: 10, font: body, color: muted });
  nl();
  text('contain raw secrets. Passcodes, seed phrases, and Shamir shares', { size: 10, font: body, color: muted });
  nl();
  text('are released only via the dead-man-switch coordinator at', { size: 10, font: body, color: muted });
  nl();
  text('wasiyat.yourtube.in/release with the holder unique ID.', { size: 10, font: body, color: muted });
  hr(indigo);
  nl(8);

  // ───────────── Readiness ─────────
  text(`Readiness score: ${v.readiness.score} / 100`, { size: 13, font: bold, color: crimson });
  nl();
  text(v.readiness.next_action, { size: 11, color: muted });
  hr();

  // ───────────── Nominees ─────────
  text('NOMINEES', { size: 14, font: bold, color: indigo });
  nl();
  if (v.nominees.length === 0) {
    text('No nominees recorded.', { color: muted });
    nl();
  } else {
    for (const n of v.nominees) {
      text(`${n.contact_priority}. ${n.display_name} (${NOMINEE_DISPLAY[n.relation].en})`, { font: bold });
      nl();
      const bits: string[] = [];
      if (n.aadhaar_last4) bits.push(`Aadhaar ****${n.aadhaar_last4}`);
      if (n.is_shamir_holder) bits.push('Shamir share holder');
      if (n.digilocker_id) bits.push(`DigiLocker: ${n.digilocker_id}`);
      if (bits.length > 0) {
        text(bits.join(' · '), { size: 10, color: muted, indent: 12 });
        nl();
      }
      if (n.notes) {
        text(`Note: ${n.notes}`, { size: 10, font: body, indent: 12 });
        nl();
      }
      nl(6);
    }
  }
  hr();

  // ───────────── Assets ───────────
  text('ASSETS', { size: 14, font: bold, color: indigo });
  nl();
  if (v.assets.length === 0) {
    text('No assets recorded.', { color: muted });
    nl();
  } else {
    const byCategory = groupAssetsByCategory(v.assets);
    for (const [cat, items] of byCategory) {
      const disp = ASSET_DISPLAY[cat];
      text(`${disp.en} (${disp.hi}) — ${items.length} entr${items.length === 1 ? 'y' : 'ies'}`, { font: bold, size: 12, color: crimson });
      nl();
      for (const a of items) {
        text(`• ${a.institution} ****${a.identifier_last4}`, { indent: 12 });
        nl();
        text(`  est. value ${formatINR(a.estimated_value_paise)}`, { size: 10, color: muted, indent: 12, font: mono });
        nl();
        if (a.custodian_notes) {
          text(`  proof: ${truncate(a.custodian_notes, 90)}`, { size: 10, color: muted, indent: 12 });
          nl();
        }
        const noms = a.nominee_ids.map((id) => v.nominees.find((n) => n.id === id)?.display_name).filter(Boolean).join(', ');
        if (noms) {
          text(`  nominee: ${noms}`, { size: 10, color: muted, indent: 12 });
          nl();
        }
        nl(4);
      }
    }
  }
  hr();

  // ───────────── Documents ─────────
  text('DOCUMENT INDEX', { size: 14, font: bold, color: indigo });
  nl();
  if (v.documents.length === 0) {
    text('No documents indexed.', { color: muted });
    nl();
  } else {
    for (const d of v.documents) {
      text(`• ${d.category.replace(/_/g, ' ')}: ${d.title} (${d.size_kb} KB)`, { indent: 6 });
      nl();
      if (d.notes) {
        text(`  ${truncate(d.notes, 110)}`, { size: 10, color: muted, indent: 12 });
        nl();
      }
    }
  }
  hr();

  // ───────────── Wishes ───────────
  text('LAST WISHES', { size: 14, font: bold, color: indigo });
  nl();
  if (v.last_wishes.length === 0) {
    text('No wishes recorded.', { color: muted });
    nl();
  } else {
    const ordered = [...v.last_wishes].sort(priorityOrder);
    for (const w of ordered) {
      text(`[${w.priority.toUpperCase()}] ${w.category.replace(/_/g, ' ')}`, { font: bold, size: 11, color: w.priority === 'must' ? crimson : ink });
      nl();
      drawWrapped(page, body, w.instruction, { x: 72, y, size: 11, maxWidth: 460, color: ink }, () => nl());
      nl(6);
    }
  }
  hr();

  // ───────────── Crypto pointer ────
  if (v.crypto_seeds.length > 0) {
    text('CRYPTO WALLETS', { size: 14, font: bold, color: indigo });
    nl();
    text('The following wallets are sharded with Shamir 3-of-5. Three', { size: 10, color: muted });
    nl();
    text('Shamir-holder nominees must coordinate at:', { size: 10, color: muted });
    nl();
    text('wasiyat.yourtube.in/release', { size: 11, font: mono, color: indigo });
    nl(16);
    for (const s of v.crypto_seeds) {
      text(`• ${s.wallet_name} (${s.chain})`, { indent: 6 });
      nl();
      if (s.hint) {
        text(`  hint: ${s.hint}`, { size: 10, color: muted, indent: 12 });
        nl();
      }
      text(`  est. value ${formatINR(s.estimated_value_paise)}`, { size: 10, color: muted, indent: 12, font: mono });
      nl(6);
    }
    hr();
  }

  // ───────────── Subscriptions ────
  if (v.subscriptions.length > 0) {
    text('SUBSCRIPTIONS', { size: 14, font: bold, color: indigo });
    nl();
    text('Cancel or transfer to avoid continued billing.', { size: 10, color: muted });
    nl(16);
    for (const s of v.subscriptions) {
      const tag = s.family_impact === 'critical' ? '[KEEP]' : s.family_impact === 'inconvenient' ? '[REVIEW]' : '[CANCEL]';
      text(`${tag} ${s.service} — ${formatINR(s.monthly_paise)}/mo`, { size: 11, indent: 6, font: s.family_impact === 'critical' ? bold : body });
      nl();
      if (s.cancellation_url) {
        text(`  cancel: ${s.cancellation_url}`, { size: 10, color: muted, indent: 12, font: mono });
        nl();
      }
      nl(2);
    }
    hr();
  }

  // ───────────── Signature ────────
  nl(20);
  text('EXECUTOR ACKNOWLEDGEMENT', { size: 12, font: bold, color: indigo });
  nl(18);
  page.drawLine({ start: { x: 60, y }, end: { x: 250, y }, thickness: 0.5, color: ink });
  page.drawLine({ start: { x: 300, y }, end: { x: 490, y }, thickness: 0.5, color: ink });
  nl(12);
  text('Signature', { size: 9, color: muted, indent: 0 });
  text('Date', { size: 9, color: muted, indent: 240 });

  // Footer
  nl(40);
  page.drawLine({ start: { x: 60, y }, end: { x: 535, y }, thickness: 0.3, color: muted });
  nl(10);
  text('Generated by Wasiyat. On-device. Encrypted. wasiyat.yourtube.in', { size: 8, color: muted });
  nl();
  text(`Schema v${v.schema_version} · Generated ${new Date(v.generated_at).toISOString()}`, { size: 8, color: muted, font: mono });

  return doc.save();
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

function groupAssetsByCategory(assets: Asset[]): Map<Asset['category'], Asset[]> {
  const m = new Map<Asset['category'], Asset[]>();
  for (const a of assets) {
    const arr = m.get(a.category) ?? [];
    arr.push(a);
    m.set(a.category, arr);
  }
  return m;
}

function priorityOrder(a: LastWish, b: LastWish): number {
  const order = { must: 0, preferred: 1, nice_to_have: 2 } as const;
  return order[a.priority] - order[b.priority];
}

function drawWrapped(page: PDFPage, font: PDFFont, text: string, opts: { x: number; y: number; size: number; maxWidth: number; color: unknown }, advance: () => void): void {
  const words = text.split(/\s+/);
  let line = '';
  for (const w of words) {
    const tryLine = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(tryLine, opts.size) > opts.maxWidth) {
      page.drawText(line, opts);
      advance();
      line = w;
    } else {
      line = tryLine;
    }
  }
  if (line) {
    page.drawText(line, opts);
    advance();
  }
}

// stable reference so Nominee import isn't shaken away
export const _t: Nominee | null = null;
